// file: client/src/offline/syncEngine.js
/**
 * MODULE 09 — the background sync engine (offline-first lifecycle).
 *
 *   · Connectivity = a light reachability probe (the offline gate's circuit
 *     breaker handles instant outage detection on real requests; this loop
 *     handles idle detection + RECOVERY).
 *   · On the first session of each calendar day → full prime (download the
 *     required database). During the day → continuous delta refresh (keep
 *     local in step with the server).
 *   · On reconnect → prime-if-new-day, drain the outbox (push local changes),
 *     then refresh (drain-before-pull). Backoff with jitter while down.
 *   · French toasts via the existing pharmakon:toast CustomEvent.
 */
import apiClient from "../services/api.client.js";
import { API_BASE_URL } from "../config/app.config.js";
import { useOfflineStore, loadDevice } from "./offlineStore.js";
import { pendingOps, applyResult, refreshCounts } from "./outbox.js";
import { refreshDeltas, isPrimed, dailyPrimeIfNeeded } from "./localStore.js";

const ONLINE_TICK_MS = 15_000; // idle online heartbeat
const REFRESH_MS = 60_000; // background delta refresh cadence
const BACKOFF_BASE_MS = 8_000; // offline retry floor
const BACKOFF_CAP_MS = 120_000;

let started = false;
let timer = null;
let backoff = BACKOFF_BASE_MS;
let draining = false;
let lastRefreshMs = 0;

const hasToken = () => Boolean(localStorage.getItem("pharmakon_token"));

const toast = (message, type = "info") => {
  try {
    window.dispatchEvent(new CustomEvent("pharmakon:toast", { detail: { message, type } }));
  } catch {
    /* no-op */
  }
};

async function reachable() {
  try {
    const base = API_BASE_URL.replace(/\/api\/v\d+$/, "");
    const res = await fetch(`${base}/api/v1/stats/public`, {
      method: "HEAD",
      cache: "no-store",
      signal: AbortSignal.timeout(4000),
    });
    return res.ok || res.status === 405;
  } catch {
    return false;
  }
}

export async function drainOutbox() {
  if (draining || !hasToken()) return { drained: 0 };
  const device = loadDevice();
  if (!device) return { drained: 0 };
  const ops = await pendingOps();
  if (!ops.length) return { drained: 0 };

  draining = true;
  const st = useOfflineStore.getState();
  st.setStatus("SYNCING");
  try {
    const body = {
      device_id: device.device_id,
      ops: ops.map((row) => {
        const { id, op_type, offline_ts, user_id, numero_provisoire, payload, prev, hmac } = row;
        return { id, op_type, offline_ts, user_id, numero_provisoire, payload, prev, hmac };
      }),
    };
    const res = await apiClient.post("/sync/batch", body, { _bypassOfflineGate: true });
    const results = res.data?.results ?? [];
    let conflicts = 0;
    for (const r of results) {
      await applyResult(r.op_id, r);
      if (r.statut === "CONFLICT") conflicts += 1;
    }
    await refreshCounts();
    st.set({ lastSyncAt: new Date().toISOString() });
    const applied = results.filter((r) => ["APPLIED", "DUPLICATE"].includes(r.statut)).length;
    toast(
      `Synchronisation : ${applied} opération${applied > 1 ? "s" : ""} transmise${applied > 1 ? "s" : ""}${conflicts ? ` · ${conflicts} à vérifier` : ""}`,
      conflicts ? "warning" : "success",
    );
    await refreshDeltas(); // inbound refresh AFTER local facts reached the server
    return { drained: applied, conflicts };
  } catch (e) {
    void e; // network/server failure → ops stay pending; backoff paces retries
    return { drained: 0, error: true };
  } finally {
    draining = false;
    useOfflineStore.getState().setStatus(navigator.onLine ? "ONLINE" : "OFFLINE");
  }
}

/** Runs when the link is confirmed up after being down (or at boot online). */
async function onReconnect() {
  if (!hasToken()) return;
  try {
    await dailyPrimeIfNeeded(); // first session of a new day → full download
  } catch {
    /* prime retries on the next reconnect */
  }
  await drainOutbox(); // push pending local changes, then refresh (inside drain)
  lastRefreshMs = Date.now();
}

async function tick() {
  const st = useOfflineStore.getState();
  const up = await reachable();

  if (up) {
    backoff = BACKOFF_BASE_MS;
    if (!st.serverEnabled && hasToken()) {
      try {
        const res = await apiClient.get("/sync/status", { _bypassOfflineGate: true });
        st.setServerConfig(Boolean(res.data?.enabled), res.data);
      } catch {
        /* flag off or token mid-refresh — retry next tick */
      }
    }
    const wasOffline = st.status === "OFFLINE";
    if (st.status !== "SYNCING") st.setStatus("ONLINE");

    if (wasOffline) {
      toast("Connexion rétablie — synchronisation…", "success");
      await onReconnect();
    } else if (hasToken() && useOfflineStore.getState().isOfflineCapable()) {
      // Steady-state: drain anything pending + periodic background refresh.
      if ((await pendingOps()).length) await drainOutbox();
      else if (Date.now() - lastRefreshMs > REFRESH_MS) {
        try {
          await refreshDeltas();
          lastRefreshMs = Date.now();
        } catch {
          /* transient — next tick */
        }
      }
    }
    schedule(ONLINE_TICK_MS);
  } else {
    if (st.status !== "OFFLINE") {
      st.setStatus("OFFLINE");
      toast("Connexion perdue — les opérations sont enregistrées localement", "warning");
    }
    backoff = Math.min(backoff * 2, BACKOFF_CAP_MS);
    schedule(backoff * (0.75 + Math.random() * 0.5));
  }
}

function schedule(ms) {
  clearTimeout(timer);
  timer = setTimeout(tick, ms);
}

/** Boot once per tab. Inert when the flag is off / the terminal isn't enrolled. */
export async function startSyncEngine() {
  if (started) return;
  started = true;
  const st = useOfflineStore.getState();

  if (hasToken()) {
    try {
      const res = await apiClient.get("/sync/status", { _bypassOfflineGate: true });
      st.setServerConfig(Boolean(res.data?.enabled), res.data);
    } catch {
      /* offline boot — keep the persisted flag (set on a prior online session) */
    }
  }
  st.set({ primed: await isPrimed() });
  await refreshCounts();

  // First session of the day → ensure the local database is current.
  if (st.serverEnabled && loadDevice() && navigator.onLine) {
    onReconnect().catch(() => {});
  }

  window.addEventListener("online", () => schedule(200));
  window.addEventListener("offline", () => {
    useOfflineStore.getState().setStatus("OFFLINE");
    toast("Hors ligne — les ventes restent possibles", "warning");
  });
  window.addEventListener("focus", () => schedule(500));
  schedule(1_000);
}
